import { useEffect, useState } from 'react';
import { getBayes, getBayesCaso } from '@/lib/api';
import { useDateRange, useDateRangeKey } from '@/lib/dateRange';

interface Ofensor { code: string; label: string; n_casos: number; woe: number }
interface Caso {
  processo_id: number; score: number; band: string; org_id: number | null;
  prior_pct: number; intrinseco: number; comportamental: number; contexto: number;
  maior_ofensor: string | null; maior_apoiador: string | null;
}
interface Tendencia { dia: string; n: number; avg_score: number; avg_p: number; pct_alta: number; pct_baixa: number }
interface Agg {
  total: number; pct_alta: number | null;
  distribuicao: Record<string, { n: number; avg: number }>;
  top_ofensores: Ofensor[]; casos: Caso[]; tendencia?: Tendencia[];
}

// chart SVG inline (sem dependência) — tendência diária do score médio + reta de tendência
function TrendChart({ data }: { data: Tendencia[] }) {
  if (!data || data.length < 2) {
    return <div className="text-xs text-slate-400">Poucos dias para tendência (mín. 2).</div>;
  }
  const W = 720, H = 180, padL = 32, padR = 12, padT = 12, padB = 24;
  const xs = data.map((_, i) => i);
  const ys = data.map((d) => d.avg_score);
  const minY = Math.max(0, Math.min(...ys) - 5);
  const maxY = Math.min(100, Math.max(...ys) + 5);
  const sx = (i: number) => padL + (i / Math.max(1, data.length - 1)) * (W - padL - padR);
  const sy = (v: number) => padT + (1 - (v - minY) / Math.max(1, maxY - minY)) * (H - padT - padB);
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${sx(i).toFixed(1)} ${sy(d.avg_score).toFixed(1)}`).join(' ');
  // regressão linear simples p/ a reta + slope (pts por dia)
  const n = data.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  const num = xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0);
  const den = xs.reduce((a, x) => a + (x - mx) ** 2, 0) || 1;
  const slope = num / den;
  const intercept = my - slope * mx;
  const y0 = slope * 0 + intercept, y1 = slope * (n - 1) + intercept;
  const slopeDia = slope; // pts de score por dia
  const subindo = slopeDia > 0.05, caindo = slopeDia < -0.05;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-sm font-semibold ${subindo ? 'text-emerald-600' : caindo ? 'text-red-600' : 'text-slate-500'}`}>
          {subindo ? '▲ melhorando' : caindo ? '▼ piorando' : '▬ estável'}
        </span>
        <span className="text-xs text-slate-400">
          {slopeDia >= 0 ? '+' : ''}{(slopeDia).toFixed(2)} pts de score/dia ({(slopeDia * 7 >= 0 ? '+' : '')}{(slopeDia * 7).toFixed(1)}/semana)
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 200 }}>
        {[minY, (minY + maxY) / 2, maxY].map((v, i) => (
          <g key={i}>
            <line x1={padL} y1={sy(v)} x2={W - padR} y2={sy(v)} stroke="#f1f5f9" />
            <text x={4} y={sy(v) + 3} fontSize="9" fill="#94a3b8">{Math.round(v)}</text>
          </g>
        ))}
        {/* reta de tendência */}
        <line x1={sx(0)} y1={sy(y0)} x2={sx(n - 1)} y2={sy(y1)}
          stroke={subindo ? '#10b981' : caindo ? '#ef4444' : '#94a3b8'} strokeWidth="1.5" strokeDasharray="4 3" />
        {/* série */}
        <path d={path} fill="none" stroke="#6366f1" strokeWidth="2" />
        {data.map((d, i) => (
          <circle key={i} cx={sx(i)} cy={sy(d.avg_score)} r="2.5" fill="#6366f1">
            <title>{d.dia}: score {d.avg_score} · {d.n} casos · {d.pct_alta}% alta</title>
          </circle>
        ))}
        <text x={padL} y={H - 6} fontSize="9" fill="#94a3b8">{data[0].dia?.slice(5)}</text>
        <text x={W - padR} y={H - 6} fontSize="9" fill="#94a3b8" textAnchor="end">{data[n - 1].dia?.slice(5)}</text>
      </svg>
    </div>
  );
}
interface Termo { code: string; label: string; woe: number; pontos: number }

const bandColor: Record<string, string> = {
  alta: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  media: 'text-amber-600 bg-amber-50 border-amber-200',
  baixa: 'text-red-600 bg-red-50 border-red-200',
};

// célula de contribuição em pontos (+verde / −vermelho / 0 cinza)
function Pts({ v }: { v: number }) {
  if (!v) return <span className="text-slate-300">·</span>;
  const c = v > 0 ? 'text-emerald-600' : 'text-red-600';
  return <span className={`font-medium ${c}`}>{v > 0 ? '+' : ''}{v}</span>;
}

export function BayesPage() {
  const { apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [agg, setAgg] = useState<Agg | null>(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<any | null>(null);

  useEffect(() => {
    setLoading(true);
    getBayes(apiParams).then((r) => setAgg(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const openCaso = async (pid: number) => {
    setSel({ loading: true, processo_id: pid });
    try { setSel((await getBayesCaso(pid)).data); } catch { setSel(null); }
  };

  if (loading) return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!agg) return <div className="p-6 text-slate-400">Sem dados de confiança ainda.</div>;

  const d = agg.distribuicao || {};
  const maxOf = Math.max(1, ...(agg.top_ofensores || []).map((o) => o.n_casos));

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Índice de Confiança</h1>
        <p className="text-sm text-slate-500">Probabilidade Bayesiana de o cálculo estar correto. Composição: <b>P = Base + Intrínseco + Comportamental + Contexto</b>.</p>
      </div>

      {/* KPI + distribuição */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">% de casos ALTA confiança</div>
          <div className="text-3xl font-bold text-emerald-600">{agg.pct_alta ?? '—'}%</div>
          <div className="text-xs text-slate-400 mt-1">North Star · {agg.total} casos na janela</div>
        </div>
        {(['alta', 'media', 'baixa'] as const).map((b) => (
          <div key={b} className="rounded-xl border border-slate-200 p-4 bg-white">
            <div className="text-xs text-slate-500 capitalize">Confiança {b}</div>
            <div className="text-2xl font-bold text-slate-800">{d[b]?.n ?? 0}</div>
            <div className="text-xs text-slate-400 mt-1">score médio {d[b]?.avg ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Tendência diária do P */}
      <div className="rounded-xl border border-slate-200 p-4 bg-white">
        <div className="text-sm font-semibold text-slate-700 mb-1">Tendência da confiança ao longo do tempo</div>
        <div className="text-xs text-slate-400 mb-3">
          Score médio dos casos por <b>dia de criação</b>. A reta tracejada mostra se a qualidade está
          melhorando (▲) ou piorando (▼) com o tempo.
        </div>
        <TrendChart data={agg.tendencia || []} />
      </div>

      {/* Pareto de ofensores */}
      <div className="rounded-xl border border-slate-200 p-4 bg-white">
        <div className="text-sm font-semibold text-slate-700 mb-1">Maiores ofensores da confiança</div>
        <div className="text-xs text-slate-400 mb-3">O que mais derruba a confiança — a fila de trabalho.</div>
        <div className="space-y-1.5">
          {(agg.top_ofensores || []).map((o) => (
            <div key={o.code} className="flex items-center gap-3">
              <div className="w-56 text-xs text-slate-600 truncate" title={o.label}>{o.label}</div>
              <div className="flex-1 bg-slate-100 rounded h-3.5 overflow-hidden">
                <div className="bg-red-400 h-3.5" style={{ width: `${(o.n_casos / maxOf) * 100}%` }} />
              </div>
              <div className="w-16 text-right text-xs text-slate-500">{o.n_casos}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabela de TODOS os casos com a decomposição por grupo */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Casos ({agg.casos?.length || 0}) · clique para a decomposição completa
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Caso</th>
                <th className="px-3 py-2 text-right font-medium">P</th>
                <th className="px-3 py-2 text-left font-medium">Banda</th>
                <th className="px-3 py-2 text-right font-medium" title="Taxa-base (prior)">Base</th>
                <th className="px-3 py-2 text-right font-medium">Intrínseco</th>
                <th className="px-3 py-2 text-right font-medium">Comport.</th>
                <th className="px-3 py-2 text-right font-medium">Contexto</th>
                <th className="px-3 py-2 text-left font-medium">Maior ofensor</th>
              </tr>
            </thead>
            <tbody>
              {(agg.casos || []).map((r) => (
                <tr key={r.processo_id} onClick={() => openCaso(r.processo_id)}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-medium text-slate-700">#{r.processo_id}</td>
                  <td className="px-3 py-2 text-right font-bold text-slate-800">{r.score}%</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs border ${bandColor[r.band] || ''}`}>{r.band}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">{r.prior_pct}%</td>
                  <td className="px-3 py-2 text-right"><Pts v={r.intrinseco} /></td>
                  <td className="px-3 py-2 text-right"><Pts v={r.comportamental} /></td>
                  <td className="px-3 py-2 text-right"><Pts v={r.contexto} /></td>
                  <td className="px-3 py-2 text-slate-500 text-xs truncate max-w-[180px]" title={r.maior_ofensor || ''}>{r.maior_ofensor || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card de detalhe (duplo-clique) */}
      {sel && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setSel(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">Caso #{sel.processo_id}</h3>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setSel(null)}>✕</button>
            </div>
            {sel.loading ? <div className="text-slate-400">Carregando…</div>
              : !sel.found ? <div className="text-slate-400">Sem índice computado.</div>
              : (
              <>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl font-bold text-slate-800">{sel.score}%</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs border ${bandColor[sel.band] || ''}`}>{sel.band}</span>
                </div>
                <div className="text-xs text-slate-500 mb-3">
                  Base {sel.prior_pct}% &nbsp;+&nbsp; Intrínseco <Pts v={sel.grupos?.intrinseco?.pontos || 0} /> &nbsp;+&nbsp;
                  Comport. <Pts v={sel.grupos?.comportamental?.pontos || 0} /> &nbsp;+&nbsp; Contexto <Pts v={sel.grupos?.contexto?.pontos || 0} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-emerald-600 mb-1">Apoiadores</div>
                    {(sel.apoiadores || []).length === 0 && <div className="text-xs text-slate-400">—</div>}
                    {(sel.apoiadores || []).map((t: Termo) => (
                      <div key={t.code} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-600 truncate mr-2" title={t.label}>{t.label}</span>
                        <span className="text-emerald-600 font-medium">+{t.pontos}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-red-600 mb-1">Ofensores</div>
                    {(sel.ofensores || []).length === 0 && <div className="text-xs text-slate-400">—</div>}
                    {(sel.ofensores || []).map((t: Termo) => (
                      <div key={t.code} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-600 truncate mr-2" title={t.label}>{t.label}</span>
                        <span className="text-red-600 font-medium">{t.woe}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
